import { Hubspot, Salesforce } from '@clearkit/icons';
import classnames from 'classnames';
import {
  CKAvatar,
  CKCompanyLogo,
  CKContainerProps,
  CKIconBox,
  CKLayoutGrid,
  CKLayoutHorizontal,
  CKSkeleton,
  CKTooltip,
  cloneChildren,
} from 'clearkit';
import { capitalize } from 'lodash';

import { Prospect } from '~/generated/graphql';

import { FauxCheckbox } from './FauxCheckbox';
import { Tokens } from './Tokens';

type ListItemProps = Omit<CKContainerProps, 'onSelect'> & {
  prospect: Prospect;
  isSelected: boolean;
  onSelect?: (id: string) => void;
};

const AVATAR_IMAGE_SIZE = 40;

const CompanyBlock = ({ prospect }: { prospect: Prospect }) => (
  <CKLayoutHorizontal
    alignItems="start"
    className="min-w-0 @item/list:pl-6 @item/list:border-l grow shrink basis-full"
    justifyContent="start"
    wrap="nowrap"
  >
    <CKCompanyLogo size={AVATAR_IMAGE_SIZE} src={prospect.companyLogo ?? ''} />
    <div className="text-xs text-gray-600">
      <h3 className="font-medium text-gray-900 truncate whitespace-nowrap">
        {prospect.companyName}
      </h3>
      <p className="truncate whitespace-nowrap">{prospect.companyDomain}</p>
    </div>
  </CKLayoutHorizontal>
);

const PersonBlock = ({ prospect }: { prospect: Prospect }) => (
  <CKLayoutHorizontal
    alignItems="start"
    className="min-h-0 @container basis-full shrink grow"
    justifyContent="start"
    wrap="nowrap"
  >
    <CKAvatar
      className="shrink-0"
      name={prospect.name}
      size={AVATAR_IMAGE_SIZE}
      src={prospect.avatar || ''}
      variant="user"
    />
    <CKLayoutHorizontal className="flex-wrap shrink grow @item-small:flex-nowrap">
      <div className="text-sm basis-full shrink grow">
        <h3 className="font-medium truncate whitespace-nowrap">
          {prospect.name}
        </h3>
        <p className="text-xs text-gray-600 truncate whitespace-nowrap">
          {prospect.title}
        </p>
      </div>
      <Tokens
        hasEmail={prospect.email}
        hasLinkedIn={prospect.linkedin}
        hasPhone={prospect.phone}
        hasTwitter={prospect.twitter}
        isDealMaker={false}
      />
    </CKLayoutHorizontal>
  </CKLayoutHorizontal>
);

type CRMTokenProps = CKContainerProps & {
  activeClassName: string;
  service: 'salesforce' | 'hubspot';
  inCrm: boolean;
  isLoading?: boolean;
};

const CRMToken = ({
  activeClassName,
  children,
  inCrm,
  service,
  isLoading = false,
}: CRMTokenProps) => {
  const inCrmIntegration = false;
  const notConnectedMessage = 'CRM integration is not enabled';
  const message = `Prospect ${!inCrm ? 'not ' : ''}in ${capitalize(service)}`;

  return (
    <CKSkeleton className="w-6 h-6 rounded" isLoading={isLoading}>
      <CKTooltip
        targetClassName="flex basis-auto shrink-0 grow-0"
        tooltip={inCrmIntegration ? message : notConnectedMessage}
      >
        <CKIconBox className="!p-1">
          {cloneChildren({
            children,
            newProps: {
              className: classnames('text-gray-300', {
                [activeClassName]: inCrm,
              }),
              height: 16,
              width: 16,
            },
          })}
        </CKIconBox>
      </CKTooltip>
    </CKSkeleton>
  );
};

export const ListItem = ({
  className,
  prospect,
  isSelected,
  onSelect,
}: ListItemProps) => {
  const { id } = prospect;
  const handleSelect = () => {
    onSelect?.(id);
  };

  /**
   * @todo wire these up when can show what records are in CRM
   */
  const showCRMtokens = false;
  const hasSalesforce = false;
  const hasHubspot = false;

  return (
    <label
      className={classnames(
        className,
        'ck-box px-4 py-3 ck-focus-ring hover:border-blue hover:bg-blue-50 cursor-pointer ',
        {
          'bg-transparent': !isSelected,
        },
      )}
      htmlFor={id}
    >
      <input
        className="sr-only"
        id={id}
        onChange={handleSelect}
        type="checkbox"
      />
      <CKLayoutGrid
        className="flex flex-nowrap gap-6 @item/list:grid @item/list:gap-4"
        gridTemplateColumns="auto 1fr auto"
        gridTemplateRows="auto"
      >
        <FauxCheckbox checked={isSelected} className="mt-4" />
        <CKLayoutGrid
          className="flex flex-wrap min-w-0 basis-full shrink grow @item/list:grid @item/list:bg-transparent"
          gap="1.5rem"
          gridTemplateColumns="4fr 3fr"
        >
          <PersonBlock prospect={prospect} />
          <CompanyBlock prospect={prospect} />
        </CKLayoutGrid>
        {showCRMtokens ? (
          <CKLayoutHorizontal
            className="items-center pl-3 text-gray-300 border-l border-gray-200"
            gap=".75rem"
            justifyContent="center"
          >
            <CRMToken
              activeClassName="text-salesforce"
              inCrm={hasSalesforce}
              service="salesforce"
            >
              <Salesforce />
            </CRMToken>
            <CRMToken
              activeClassName="text-hubspot"
              inCrm={hasHubspot}
              service="hubspot"
            >
              <Hubspot />
            </CRMToken>
          </CKLayoutHorizontal>
        ) : null}
      </CKLayoutGrid>
    </label>
  );
};
